import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Chip, CircularProgress, Grid, Card, CardContent } from '@mui/material';
import { galleryService } from '../services/api';
import { ArrowBack, Image as ImageIcon, VideoLibrary, Tag } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getImageUrl, ASSETS } from '../utils/assets';
import SimplePageBanner from '../components/SimplePageBanner';
import SiteStatsItems from '../components/SiteStatsItems';
import { bodyTextSx, cardSx, BRAND, COLORS, sectionPaddingSx, cardHeadingSx } from '../theme/pageStyles';

const GalleryDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  useEffect(() => {
    const fetchGalleryItem = async () => {
      try {
        setLoading(true);
        const response = await galleryService.getGalleryById(id);
        const data = response.data?.data || response.data;
        setItem(data);
      } catch (error) {
        console.error('Error fetching gallery item:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryItem();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: BRAND.green }} />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ backgroundColor: COLORS.warm, minHeight: '50vh', py: 6 }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/gallery')}
            sx={{ color: BRAND.green, mb: 4, fontFamily: '"Poppins", sans-serif', fontWeight: 500 }}
          >
            Back to Gallery
          </Button>
          <Typography sx={{ ...cardHeadingSx, color: COLORS.body }}>Gallery item not found</Typography>
        </Container>
      </Box>
    );
  }

  const media = item.media && item.media.length > 0 ? item.media : [];
  const currentMedia = media[selectedMediaIndex];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Gallery"
        title={item.title}
        description={item.description || 'Photos and videos from our programs and community events.'}
      />

      <Box sx={{ backgroundColor: COLORS.warm, minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ width: '100%', px: { xs: 2, sm: 3 }, pt: 4, pb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/gallery')} sx={{ color: BRAND.green, mb: 2, textTransform: 'none', fontSize: '1rem', fontFamily: '"Poppins", sans-serif', fontWeight: 500, '&:hover': { backgroundColor: 'rgba(71, 124, 46, 0.08)' } }}>Back to Gallery</Button>
        </Container>
      <Box sx={sectionPaddingSx}>
      <Container maxWidth="xl" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Media Display - Left Side */}
          <Grid item xs={12} md={7}>
            <Card sx={{
              border: 'none',
              ...cardSx,
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#000'
            }} elevation={0}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                {currentMedia ? (
                  currentMedia.media_type === 'video' ? (
                    <Box
                      component="iframe"
                      src={getImageUrl(currentMedia.media_url)}
                      sx={{
                        width: '100%',
                        height: 550,
                        border: 'none'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <Box
                      component="img"
                      src={getImageUrl(currentMedia.media_url)}
                      alt={item.title}
                      sx={{
                        width: '100%',
                        height: 550,
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  )
                ) : (
                  <Box sx={{
                    width: '100%',
                    height: 550,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999'
                  }}>
                    <Typography>No media available</Typography>
                  </Box>
                )}

                {/* Media Type Badge */}
                {currentMedia && (
                  <Box sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 10
                  }}>
                    <Chip
                      icon={currentMedia.media_type === 'video' ? <VideoLibrary /> : <ImageIcon />}
                      label={currentMedia.media_type === 'video' ? 'Video' : 'Image'}
                      sx={{ backgroundColor: BRAND.green, color: 'white', fontWeight: 'bold', padding: '8px 12px' }}
                    />
                  </Box>
                )}
              </Box>
            </Card>

            {/* Media Thumbnails */}
            {media.length > 1 && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
                {media.map((m, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedMediaIndex(index)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: selectedMediaIndex === index ? `3px solid ${BRAND.green}` : '2px solid transparent',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: BRAND.green,
                        transform: 'scale(1.05)'
                      },
                      minWidth: 100,
                      height: 100,
                      backgroundColor: '#000'
                    }}
                  >
                    {m.media_type === 'video' ? (
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#1a1a1a',
                        color: 'white'
                      }}>
                        <VideoLibrary />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={getImageUrl(m.media_url)}
                        alt={`Thumbnail ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Details - Right Side */}
          <Grid item xs={12} md={5}>
            {/* Title and Description Card */}
            <Card sx={{ ...cardSx, mb: 3 }} elevation={0}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography sx={{ ...cardHeadingSx, color: BRAND.green, mb: 2 }}>{item.title}</Typography>
                <Typography sx={{ ...bodyTextSx, lineHeight: 1.8, mb: 3 }}>
                  {item.description || 'No description available'}
                </Typography>
                {item.category && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<Tag />}
                      label={item.category}
                      sx={{
                        backgroundColor: COLORS.cream,
                        color: BRAND.green,
                        fontWeight: 600,
                        height: 'auto',
                        padding: '8px 12px',
                        fontFamily: '"Poppins", sans-serif',
                        '& .MuiChip-icon': { marginLeft: '8px' }
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Media Info Card */}
            {currentMedia && (
              <Card sx={cardSx} elevation={0}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography sx={{ ...cardHeadingSx, mb: 3 }}>Media Information</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                      <Typography sx={{ ...bodyTextSx, fontWeight: 500 }}>Type</Typography>
                      <Chip
                        icon={currentMedia.media_type === 'video' ? <VideoLibrary /> : <ImageIcon />}
                        label={currentMedia.media_type === 'video' ? 'Video' : 'Image'}
                        size="small"
                        sx={{ backgroundColor: COLORS.cream, color: BRAND.green, fontWeight: 'bold', fontFamily: '"Poppins", sans-serif' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                      <Typography sx={{ ...bodyTextSx, fontWeight: 500 }}>Total Media Items</Typography>
                      <Typography sx={{ ...bodyTextSx, fontWeight: 700, color: BRAND.green }}>{media.length}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ ...bodyTextSx, fontWeight: 500 }}>Current Item</Typography>
                      <Typography sx={{ ...bodyTextSx, fontWeight: 700, color: BRAND.green }}>{selectedMediaIndex + 1} / {media.length}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
      </Box>
      </Box>

      {/* Join With Us - stats bar (same as Home/Contact) */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography sx={{ fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 24, md: 27 }, color: '#313530', textAlign: 'center', mb: '25px' }}>Join With Us</Typography>
          <Typography component="h2" sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 26, sm: 34, md: 40 }, lineHeight: 1.25, letterSpacing: '0.01em', color: '#2D2929', textAlign: 'center', mb: { xs: '30px', md: '50px' } }}>
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box sx={{ position: 'relative', overflow: 'hidden', maxWidth: 1200, mx: 'auto', borderRadius: { xs: 16, md: '23px' }, minHeight: { xs: 200, md: 238 }, display: 'flex', flexDirection: 'column', border: '1px solid #c4a574', backgroundImage: `url(${ASSETS.parallax})`, backgroundSize: 'cover', backgroundPosition: 'center', '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: 'rgba(36, 88, 78, 0.63)', borderRadius: '23px' } }}>
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', justifyContent: 'space-evenly', alignItems: 'center', flex: 1, minHeight: { xs: 200, md: 238 }, gap: { xs: 3, sm: 0 }, py: { xs: 4, md: 5 }, px: { xs: 3, md: 4 } }}>
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default GalleryDetailPage;
